/*
  Carrossel de avaliações — um card por vez, vizinhos espiando nas laterais.

  A rolagem é nativa (scroll-snap): o arraste, o embalo do trackpad e o toque
  saem de graça, e o JS só cuida do índice, dos dots e do autoplay.

  O loop infinito é feito com três cópias da lista (antes / real / depois) e a
  gente vive sempre na do meio. Quando a rolagem para em outra cópia, o
  scrollLeft é corrigido em um conjunto inteiro sem animação — como o conteúdo
  é idêntico naquele ponto, o salto não aparece. É o mesmo truque dos clones do
  slick, mas sem reimplementar a rolagem.

  Ser um custom element é o que faz isso sobreviver ao editor de temas: quando
  a seção é re-renderizada, o connectedCallback roda de novo sozinho. Um init
  amarrado a um "document ready" só rodaria na carga da página.
*/
if (!customElements.get('customer-reviews')) {
  customElements.define(
    'customer-reviews',
    class CustomerReviews extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-cr-viewport]');
        this.track = this.querySelector('[data-cr-track]');
        if (!this.viewport || !this.track) return;

        this.real = Array.from(this.track.querySelectorAll('[data-cr-slide]:not([data-cr-clone])'));
        this.count = this.real.length;
        if (!this.count) return;

        this.dots = Array.from(this.querySelectorAll('[data-cr-dot]'));
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.interval = Number(this.dataset.interval) || 5000;
        this.loop = this.dataset.loop === 'true' && this.count > 1;
        this.index = 0;
        this.visible = true;

        if (this.loop) this.buildClones();
        this.slides = Array.from(this.track.children);
        // Onde começa o conjunto real dentro do track.
        this.offset = this.loop ? this.count : 0;

        this.bind();
        this.measure();

        // O primeiro posicionamento precisa do layout já resolvido, e a fonte
        // costuma chegar depois — nos dois momentos o card muda de largura.
        requestAnimationFrame(() => this.goTo(0, 'auto'));
        document.fonts?.ready.then(() => {
          this.measure();
          this.goTo(this.index, 'auto');
        });

        this.play();
      }

      disconnectedCallback() {
        this.stop();
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('visibilitychange', this.onVisibility);
        clearTimeout(this.settleTimer);
      }

      bind() {
        this.onScroll = this.onScroll.bind(this);
        this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            this.goTo(Number(dot.dataset.index));
            // Recomeça a contagem, senão o próximo salto automático viria
            // quase junto do clique.
            this.play();
          });
        });

        this.addEventListener('pointerenter', () => this.stop());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('pointerleave', () => this.play());
        this.addEventListener('focusout', () => this.play());

        // Arrastar com o mouse. Container de rolagem nativo só anda com toque,
        // trackpad ou barra de rolagem — no desktop, segurar o botão e puxar
        // não faz nada. O toque continua nativo: só o mouse passa por aqui.
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);

        this.onVisibility = () => (document.hidden ? this.stop() : this.play());
        document.addEventListener('visibilitychange', this.onVisibility);

        // Fora da tela não precisa girar — economiza trabalho e não "gasta"
        // avaliações enquanto ninguém está olhando.
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.visible = entry.isIntersecting;
            this.visible ? this.play() : this.stop();
          },
          { threshold: 0.2 }
        );
        this.observer.observe(this);

        // A largura do card vem do layout, então qualquer mudança de largura
        // invalida a medida. O ResizeObserver pega mudanças de container; o
        // listener de resize é o fallback (neste tema o observer nem sempre
        // entrega eventos).
        this.onResize = () => {
          this.measure();
          this.goTo(this.index, 'auto');
        };
        window.addEventListener('resize', this.onResize);
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.viewport);
      }

      /* Três cópias da lista para o loop nunca chegar na borda, em nenhuma das
         duas direções. As cópias saem do leitor de tela e da tabulação. */
      buildClones() {
        this.track.querySelectorAll('[data-cr-clone]').forEach((node) => node.remove());

        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();

        this.real.forEach((slide) => {
          before.appendChild(this.cloneSlide(slide));
          after.appendChild(this.cloneSlide(slide));
        });

        this.track.insertBefore(before, this.track.firstChild);
        this.track.appendChild(after);
      }

      cloneSlide(slide) {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-cr-clone', '');
        // Mesma URL da original: o navegador serve do cache, sem request novo.
        clone.querySelectorAll('img').forEach((img) => img.setAttribute('alt', ''));
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        return clone;
      }

      measure() {
        const first = this.slides[0];
        const second = this.slides[1];
        if (!first) return;
        const rect = first.getBoundingClientRect();
        this.stride = second ? second.getBoundingClientRect().left - rect.left : rect.width;
        this.setWidth = this.stride * this.count;
      }

      behavior() {
        return this.motion.matches ? 'auto' : 'smooth';
      }

      /* Índice, dentro do track, do slide mais próximo do centro do viewport. */
      nearest() {
        const view = this.viewport.getBoundingClientRect();
        const center = view.left + view.width / 2;
        let best = 0;
        let bestDistance = Infinity;

        this.slides.forEach((slide, i) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = i;
          }
        });

        return best;
      }

      centerOn(slide, behavior) {
        if (!slide) return;
        const view = this.viewport.getBoundingClientRect();
        const rect = slide.getBoundingClientRect();
        const delta = rect.left + rect.width / 2 - (view.left + view.width / 2);
        this.viewport.scrollTo({ left: this.viewport.scrollLeft + delta, behavior: behavior || 'auto' });
      }

      /* index é a posição na lista real (0..count-1). */
      goTo(index, behavior) {
        this.index = ((index % this.count) + this.count) % this.count;
        this.centerOn(this.slides[this.offset + this.index], behavior || this.behavior());
        this.syncDots();
        this.scheduleSettle(600);
      }

      next() {
        const current = this.nearest();
        const target = this.slides[current + 1];
        // Sem loop não existe slide depois do último: volta ao começo.
        if (!target) {
          this.goTo(0);
          return;
        }

        // O índice é atualizado na hora, não quando a rolagem assentar: o
        // evento scroll pode ser adiado ou agrupado pelo navegador (em aba
        // sem foco ele simplesmente não chega), e aí os dots ficariam parados
        // enquanto os cards passam.
        this.setIndexFrom(current + 1);
        this.centerOn(target, this.behavior());
        this.scheduleSettle(600);
      }

      setIndexFrom(position) {
        this.index = (((position - this.offset) % this.count) + this.count) % this.count;
        this.syncDots();
      }

      onPointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag = { x: event.clientX, left: this.viewport.scrollLeft };
        // O snap obrigatório briga com o arraste: ele puxa de volta a cada
        // pixel. Sai durante o gesto e volta no soltar.
        this.viewport.style.scrollSnapType = 'none';
        this.viewport.style.userSelect = 'none';
        this.viewport.setPointerCapture(event.pointerId);
      }

      onPointerMove(event) {
        if (!this.drag) return;
        this.viewport.scrollLeft = this.drag.left - (event.clientX - this.drag.x);
      }

      onPointerUp() {
        if (!this.drag) return;
        this.drag = null;
        this.viewport.style.scrollSnapType = '';
        this.viewport.style.userSelect = '';
        // Com o snap religado o navegador não reposiciona sozinho: encosta no
        // card mais próximo na mão.
        this.centerOn(this.slides[this.nearest()], this.behavior());
        this.scheduleSettle(600);
      }

      onScroll() {
        if (this.locked) return;
        this.scheduleSettle(120);
      }

      /* Um único agendamento para os dois caminhos: o arraste do usuário
         reagenda a cada evento de scroll (assenta 120ms depois do último) e o
         movimento programático usa o prazo maior, que cobre a animação. */
      scheduleSettle(delay) {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), delay);
      }

      /* Roda quando a rolagem parou: reposiciona no conjunto do meio e
         atualiza os dots. */
      settle() {
        if (this.loop) {
          const position = this.nearest();
          let shift = 0;
          if (position < this.count) shift = this.setWidth;
          else if (position >= this.count * 2) shift = -this.setWidth;

          if (shift) {
            this.locked = true;
            this.viewport.scrollLeft += shift;
            requestAnimationFrame(() => {
              this.locked = false;
            });
          }
        }

        this.index = ((this.nearest() - this.offset) % this.count + this.count) % this.count;
        this.syncDots();
      }

      syncDots() {
        this.dots.forEach((dot, i) => {
          if (i === this.index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      play() {
        this.stop();
        if (this.dataset.autoplay !== 'true' || this.motion.matches) return;
        if (document.hidden || !this.visible) return;
        this.timer = setInterval(() => this.next(), this.interval);
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
}
